import React, { useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import {FontAwesome5, Ionicons, MaterialIcons} from '@expo/vector-icons';
import { priceFormatter } from "../helpers";
import {useTranslation} from "react-i18next";
import TimeAgo from "react-native-timeago";
import {OnlineOrderContext} from "../helpers/OnlineOrderContext";

const OrderRow = ({ order, onPress }) => {
    const { resetOnlineOrder } = React.useContext(OnlineOrderContext);
    const { t } = useTranslation();

    const getBackgroundColor = useCallback(() => {
        if (order.storno || order.status === 'cancelled') {
            return styles.stornoBackground;
        } else if (order.status === 'closed' && !order.storno) {
            return styles.closedBackground;
        } else if (order.preOrder) {
            return styles.preOrderBackground;
        }
        return styles.defaultBackground;
    }, [order.storno, order.status, order.preOrder]);

    const convertPaymentType = useCallback((paymentType) => {
        switch (paymentType) {
            case "cash":
                return t("cash");
            case "creditCard":
                return t("credit_card");
            case "voucher":
                return t("voucher");
            case "vipBalance":
                return t("vip_balance");
            case "employeeBalance":
                return  t('employee_balance');
            default:
                return `${paymentType}`;
        }
    }, [t]);

    const getOrderIcon = useCallback((source) => {
        switch (source) {
            case 'terminal':
                return <MaterialIcons name="point-of-sale" size={24} color="#555" />;
            case 'phone':
                return <FontAwesome5 name="mobile-alt" size={24} color="#555" />;
            case 'webshop':
                return <Ionicons name="earth-outline" size={24} color="#555" />;
            case 'wolt':
                return <MaterialIcons name="delivery-dining" size={24} color="#00C2E8" />;
            case 'netpincer':
                return <MaterialIcons name="delivery-dining" size={24} color="#FF6900" />;
            default:
                return <MaterialIcons name="point-of-sale" size={24} color="#555" />;
        }
    }, []);

    const getOrderTypeIcon = useCallback((type) => {
        switch (type) {
            case 'takeAway':
                return <MaterialIcons name="takeout-dining" size={20} color="#555" />;
            case 'dineIn':
                return <MaterialIcons name="restaurant" size={20} color="#555" />;
            case 'online':
                return <MaterialIcons name="devices" size={20} color="#555" />;
            case 'delivery':
                return <MaterialIcons name="delivery-dining" size={20} color="#555" />;
            default:
                return <MaterialIcons name="takeout-dining" size={20} color="#555" />;
        }
    }, []);

    const getStatusIcon = useCallback((status) => {
        switch (status) {
            case 'accepted':
                return <MaterialIcons name="check-circle" size={20} color="#4CAF50" />;
            case 'preparing':
                return <MaterialIcons name="hourglass-top" size={20} color="#FF9800" />;
            case 'cancelled':
                return <MaterialIcons name="cancel" size={20} color="#F44336" />;
            case 'closed':
                return <MaterialIcons name="done-all" size={20} color="#2196F3" />;
            case 'busy':
                return <MaterialIcons name="access-time" size={20} color="#FFC107" />;
            case 'ready':
                return <MaterialIcons name="check-circle-outline" size={20} color="#4CAF50" />;
            case 'storno':
                return <MaterialIcons name="cancel" size={20} color="#F44336" />;
            default:
                return <MaterialIcons name="help-outline" size={20} color="#9E9E9E" />;
        }
    }, []);

    const getPaymentTypeIcon = useCallback((type) => {
        switch (type) {
            case 'temporary':
                return <MaterialIcons name="pending" size={20} color="#9E9E9E" />;
            case 'cash':
                return <MaterialIcons name="payments" size={20} color="#4CAF50" />;
            case 'creditCard':
                return <MaterialIcons name="credit-card" size={20} color="#2196F3" />;
            case 'szepCard':
            case 'szepCreditCard':
                return <MaterialIcons name="card-membership" size={20} color="#9C27B0" />;
            case 'voucher':
                return <MaterialIcons name="local-offer" size={20} color="#FF9800" />;
            case 'employeeBalance':
                return <MaterialIcons name="account-balance-wallet" size={20} color="#607D8B" />;
            case 'vipBalance':
                return <MaterialIcons name="card-membership" size={20} color="#9C27B0" />;
            default:
                return <MaterialIcons name="help-outline" size={20} color="#9E9E9E" />;
        }
    }, []);

    // Memoize the formatted dates
    const formattedPreOrderTime = useMemo(() => order.preOrder ? new Date(order.preOrder).toLocaleString() : '', [order.preOrder]);

    // Memoize the total items calculation
    const totalItems = useMemo(() => order.items.reduce((sum, item) => sum + item.amount, 0), [order.items]);

    // Memoize the onPress handler
    const handlePress = useCallback(() => {
        resetOnlineOrder();
        onPress(order);
    }, [resetOnlineOrder, onPress, order]);

    return (
        <TouchableOpacity onPress={handlePress} style={[styles.orderRow, getBackgroundColor()]}>
            {/* Left Section: Time and Pre-order */}
            <View style={styles.leftSection}>
                <View style={styles.timeContainer}>
                    <MaterialIcons name="access-time" size={16} color="#555" style={styles.smallIcon} />
                    <View style={{display: 'flex', flexDirection: 'columns'}}>
                        <Text style={styles.orderDate}><TimeAgo time={new Date(order.createdAt)} interval={10} /></Text>
                        <Text style={styles.orderDate}>{new Date(order.createdAt).toLocaleTimeString()}</Text>
                    </View>
                </View>
                {order.preOrder && (
                    <View style={styles.preOrderContainer}>
                        <MaterialIcons name="event" size={16} color="#FF9800" style={styles.smallIcon} />
                        <Text style={styles.preOrderText}>{formattedPreOrderTime}</Text>
                    </View>
                )}
            </View>

            {/* Order Source and Type */}
            <View style={styles.orderDetails}>
                <View style={styles.iconTextContainer}>
                    {getOrderIcon(order.orderFrom)}
                    <Text style={styles.orderOrigin}>{order.orderFrom === order.restaurantPlace ? t("terminal") : t(order.orderFrom)}</Text>
                </View>
                <View style={styles.iconTextContainer}>
                    {getOrderTypeIcon(order.orderType)}
                    <Text style={styles.orderType}>{t(order.orderType)}</Text>
                </View>
            </View>

            {/* Table Info if available */}
            <View style={styles.orderDetailsWithMargin}>
                {order?.tableId && (
                    <View style={styles.iconTextContainer}>
                        <MaterialIcons name="table-bar" size={20} color="#555" />
                        <Text style={styles.orderOrigin}>{order?.tableName}</Text>
                    </View>
                )}
            </View>

            {/* Order Code and Status */}
            <View style={styles.middleSection}>
                <Text style={styles.orderCode}>#{order.orderCode}</Text>
                <View style={styles.statusContainer}>
                    {getStatusIcon(order.status)}
                    <Text style={styles.statusText}>{t(order.status)}</Text>
                </View>
            </View>

            {/* User Information */}
            <View style={styles.userInfo}>
                {order?.userInfo && (
                    <View>
                        <View style={styles.personalInfo}>
                            <MaterialIcons name="person" size={18} color="#555" style={styles.smallIcon} />
                            <Text style={styles.flexText}>
                                {order?.userInfo?.name?.first} {order?.userInfo?.name?.last} 
                                {order?.userInfo?.phone && (
                                    <Text style={styles.phoneText}>
                                        <MaterialIcons name="phone" size={14} color="#555" /> {order?.userInfo?.phone}
                                    </Text>
                                )}
                            </Text>
                        </View>
                        {order.addressInfo && (
                            <View style={styles.addressInfo}>
                                <MaterialIcons name="location-on" size={18} color="#555" style={styles.smallIcon} />
                                <Text style={styles.flexText}>
                                    {order?.addressInfo?.zip} {order?.addressInfo?.city}, {order?.addressInfo?.address}
                                </Text>
                            </View>
                        )}
                    </View>
                )}
            </View>

            {/* Payment and Total */}
            <View style={styles.rightSection}>
                <View style={styles.paymentContainer}>
                    {getPaymentTypeIcon(order.paymentType)}
                    <Text style={styles.paymentType}>{convertPaymentType(order.paymentType)}</Text>
                </View>
                <Text style={styles.orderAmount}>{priceFormatter(order.orderSum)}</Text>
                <View style={styles.itemCountContainer}>
                    <MaterialIcons name="shopping-cart" size={16} color="#777" style={styles.smallIcon} />
                    <Text style={styles.itemCount}>{totalItems} {t("item-s")}</Text>
                </View>
            </View>
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    orderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        borderRadius: 8,
        marginVertical: 6,
        borderWidth: 1,
        borderColor: '#ccc',
    },
    leftSection: {
        flex: 1,
        alignItems: 'flex-start',
        justifyContent: 'center',
    },
    middleSection: {
        flex: 2,
        alignItems: 'center',
        justifyContent: 'center',
    },
    rightSection: {
        flex: 1,
        alignItems: 'flex-end',
        justifyContent: 'center',
    },
    orderDetails: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        flex: 1,
    },
    orderDetailsWithMargin: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        flex: 1,
        marginLeft: 5,
    },
    userInfo: {
        flex: 3,
        justifyContent: 'center',
    },
    orderAmount: {
        textAlign: 'right',
        fontSize: 16,
        fontWeight: 'bold',
        color: '#333',
        marginVertical: 4,
    },
    orderDate: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#333',
        marginLeft: 4,
    },
    orderCode: {
        fontSize: 16,
        fontWeight: 'bold',
        marginBottom: 4,
        color: '#333',
    },
    orderOrigin: {
        fontSize: 14,
        color: '#555',
        marginLeft: 4,
    },
    orderType: {
        fontSize: 14,
        color: '#555',
        marginLeft: 4,
    },
    itemCount: {
        fontSize: 14,
        color: '#777',
        marginLeft: 4,
    },
    paymentType: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#555',
        marginLeft: 4,
    },
    stornoBackground: {
        backgroundColor: 'rgba(255, 0, 0, 0.6)',
    },
    statusIndicator: {
        marginLeft: 10,
    },
    closedBackground: {
        backgroundColor: 'rgba(214,255,180,0.3)',
    },
    preOrderBackground: {
        backgroundColor: 'rgba(255, 165, 0, 0.5)',
    },
    defaultBackground: {
        backgroundColor: '#f5f5f5',
    },
    flexText: {
        flex: 1,
        marginLeft: 4,
    },
    personalInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 4,
    },
    addressInfo: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    timeContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 2,
    },
    preOrderContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 2,
    },
    preOrderText: {
        fontSize: 12,
        color: '#FF9800',
        fontWeight: 'bold',
        marginLeft: 4,
    },
    iconTextContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 4,
    },
    statusContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 4,
    },
    statusText: {
        fontSize: 14,
        color: '#555',
        marginLeft: 4,
    },
    paymentContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginBottom: 4,
    },
    itemCountContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginTop: 4,
    },
    smallIcon: {
        marginRight: 2,
    },
    phoneText: {
        fontSize: 12,
        color: '#555',
        marginLeft: 4,
    },
});

export default React.memo(OrderRow);
